// JobClose AI · PO 2026-02 · Business Texting management surface.
//
// One setup owner. Business identity (legal name, address, phone,
// email, website, EIN, representative) is sourced read-only from
// `contractor_profiles` via GET /api/orgs/me/sms-onboarding. Only the
// SMS-specific compliance attestations (estimated volume, opt-in
// method, opt-in details, age-gated declaration) are editable here,
// and only for PAID contractors.
//
// Trial / unpaid contractors see an informational-only view. There is
// no actionable form during the free trial — Business Texting is
// exclusively paid, and Twilio is JobClose-internal infrastructure.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import {
  MessageSquare, Loader2, CheckCircle2, AlertTriangle, Info, Lock,
  ExternalLink,
} from "lucide-react";

const OPT_IN_LABELS = {
  verbal: "Verbal consent (customer told me they want SMS)",
  web_form: "Web form / online intake (customer checked a consent box)",
  paper: "Paper intake or work authorization (signed consent line)",
  point_of_sale: "In person at appointment / point of sale",
  other: "Other (please describe below)",
};

const STATE_LABELS = {
  unpaid: { label: "Included with paid subscription", tone: "slate" },
  info_needed: { label: "Compliance info needed", tone: "amber" },
  ready: { label: "Ready — awaiting JobClose provisioning", tone: "blue" },
  provisioning: { label: "Provisioning your business number", tone: "blue" },
  needs_attention: { label: "Needs attention — contact support", tone: "red" },
  verification_in_progress: {
    label: "Verification in progress", tone: "blue",
  },
  live: { label: "Live — you can text customers", tone: "green" },
  rejected: { label: "Verification needs attention", tone: "red" },
};

const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-800 border-slate-300",
  amber: "bg-amber-50 text-amber-800 border-amber-300",
  blue: "bg-blue-50 text-blue-800 border-blue-300",
  green: "bg-green-50 text-green-800 border-green-300",
  red: "bg-red-50 text-red-800 border-red-300",
};

const Pill = ({ tone = "slate", children, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${TONE_CLASSES[tone]}`}
  >
    {children}
  </span>
);

function formatAddress(a) {
  if (!a) return "";
  const parts = [
    [a.street, a.unit].filter(Boolean).join(" "),
    a.city,
    [a.state, a.zip].filter(Boolean).join(" "),
  ].filter(Boolean);
  return parts.join(", ");
}

export default function BusinessTexting() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    estimated_monthly_volume: "",
    opt_in_method: "",
    opt_in_details: "",
    age_gated_declaration: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orgs/me/sms-onboarding");
      setSnap(data);
      const c = data?.compliance || {};
      setForm({
        estimated_monthly_volume:
          c.estimated_monthly_volume ?? "",
        opt_in_method: c.opt_in_method || "",
        opt_in_details: c.opt_in_details || "",
        age_gated_declaration:
          c.age_gated_declaration === true ? "true"
          : c.age_gated_declaration === false ? "false"
          : "",
      });
    } catch (err) {
      toast.error("Couldn't load your Business Texting status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e?.target?.value ?? e }));

  const canAttest = useMemo(() => {
    if (!snap?.is_paid) return false;
    if ((snap.missing_identity_fields || []).length > 0) return false;
    return true;
  }, [snap]);

  const handleSave = async () => {
    if (!canAttest) return;
    setSaving(true);
    try {
      const payload = {};
      if (String(form.estimated_monthly_volume).trim() !== "") {
        payload.estimated_monthly_volume = Number(form.estimated_monthly_volume);
      }
      if (form.opt_in_method) payload.opt_in_method = form.opt_in_method;
      if ((form.opt_in_details || "").trim() !== "") {
        payload.opt_in_details = form.opt_in_details.trim();
      }
      if (form.age_gated_declaration === "true"
          || form.age_gated_declaration === "false") {
        payload.age_gated_declaration = form.age_gated_declaration === "true";
      }
      if (Object.keys(payload).length === 0) {
        toast.error("Add at least one attestation field before saving.");
        setSaving(false);
        return;
      }
      const { data } = await api.put("/orgs/me/sms-compliance", payload);
      setSnap(data);
      toast.success(
        data.compliance_complete
          ? "Business Texting compliance saved — ready for JobClose provisioning."
          : "Saved. A few attestations still required.",
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error("Business Texting is included with the paid subscription.");
      } else if (detail?.field_errors) {
        const first = Object.values(detail.field_errors)[0];
        toast.error(first || "Please review the highlighted fields.");
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !snap) {
    return (
      <DashboardLayout title="Business Texting"
        subtitle="Customer SMS through JobClose infrastructure.">
        <div className="jc-card p-8 flex items-center gap-2 text-sm">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      </DashboardLayout>
    );
  }

  const state = snap.state;
  const pill = STATE_LABELS[state] || STATE_LABELS.info_needed;
  const prefill = snap.prefill || {};
  const identityMissing = snap.missing_identity_fields || [];
  const publicPath = snap.public_consent_path || "";
  const previewUrl = publicPath
    ? `${window.location.origin.replace(/\/$/, "")}${publicPath}`
    : "";

  return (
    <DashboardLayout
      title="Business Texting"
      subtitle="Customer SMS through JobClose infrastructure — included with your paid subscription."
    >
      <div
        data-testid="business-texting-page"
        data-state={state}
        className="max-w-3xl space-y-5"
      >
        {/* Status card */}
        <section
          data-testid="business-texting-status-card"
          className="jc-card p-5 space-y-3"
        >
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-[var(--jc-blue)]" />
            <div className="font-semibold text-sm">Current status</div>
            <div className="ml-auto">
              <Pill tone={pill.tone} testId="business-texting-state-pill">
                {pill.label}
              </Pill>
            </div>
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] flex items-start gap-1.5">
            <Info size={12} className="mt-0.5 shrink-0" />
            <p>
              Twilio is internal JobClose infrastructure. You will never
              receive a separate Twilio bill. Business Texting is
              exclusively part of your paid JobClose subscription.
              {snap?.compliance?.internal_owner_hint
                ? " · Included via internal owner entitlement." : ""}
            </p>
          </div>
          {snap?.entitlement_type === "internal_owner" && (
            <div
              data-testid="business-texting-internal-owner-chip"
              className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 inline-block"
            >
              Internal Owner — included at $0
            </div>
          )}
        </section>

        {/* Trial / unpaid — informational only, no form */}
        {!snap.is_paid && (
          <section
            data-testid="business-texting-trial-panel"
            className="jc-card p-6 border-l-4 border-l-slate-400 flex items-start gap-3"
          >
            <div className="shrink-0 w-10 h-10 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center">
              <Lock size={18} />
            </div>
            <div className="space-y-2">
              <div className="font-semibold">
                Included with your paid JobClose subscription
              </div>
              <p className="text-sm text-[var(--jc-text-soft)]">
                No setup action is required during the free trial. Once
                you convert to a paid subscription, Business Texting
                becomes available and JobClose provisions your dedicated
                business number.
              </p>
              <p className="text-xs text-[var(--jc-text-soft)]">
                Meanwhile, continue completing your normal business setup
                in <Link to="/app/setup" className="underline">Set Up Your Business</Link> —
                it is used across every JobClose feature.
              </p>
            </div>
          </section>
        )}

        {/* Paid — but identity fields missing on contractor_profiles */}
        {snap.is_paid && identityMissing.length > 0 && (
          <section
            data-testid="business-texting-identity-gap"
            className="jc-card p-5 border-l-4 border-l-amber-400 space-y-2"
          >
            <div className="flex items-center gap-2 font-semibold text-sm">
              <AlertTriangle size={16} className="text-amber-600" />
              Finish your business setup first
            </div>
            <p className="text-xs text-[var(--jc-text-soft)]">
              Business Texting reuses the information already in your
              company profile. A few required fields are missing —
              complete them in Set Up Your Business, then return here.
            </p>
            <ul
              className="text-xs text-[var(--jc-text-soft)] list-disc pl-5"
              data-testid="business-texting-identity-missing"
            >
              {identityMissing.includes("legal_business_name") && <li>Legal business name</li>}
              {identityMissing.includes("business_phone") && <li>Business phone</li>}
              {identityMissing.includes("business_email") && <li>Business email</li>}
              {identityMissing.includes("business_address") && <li>Business address (street, city, state, ZIP)</li>}
              {identityMissing.includes("ein") && <li>EIN (Federal Tax ID)</li>}
            </ul>
            <Link
              to="/app/setup?step=business_and_trades"
              data-testid="business-texting-open-setup-cta"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary"
            >
              Open Set Up Your Business
            </Link>
          </section>
        )}

        {/* Paid + identity complete — canonical prefill display + attestations */}
        {snap.is_paid && identityMissing.length === 0 && (
          <>
            <section
              data-testid="business-texting-prefill-card"
              className="jc-card p-5 space-y-3"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-600" />
                <div className="font-semibold text-sm">
                  Business identity — from your company profile
                </div>
                <Link
                  to="/app/company-profile"
                  data-testid="business-texting-edit-identity-link"
                  className="ml-auto text-xs font-semibold text-[var(--jc-blue)] hover:underline inline-flex items-center gap-1"
                >
                  Edit <ExternalLink size={11} />
                </Link>
              </div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div>
                  <dt className="text-[var(--jc-text-soft)]">Legal business name</dt>
                  <dd data-testid="business-texting-prefill-name">
                    {prefill.legal_business_name || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--jc-text-soft)]">Business phone</dt>
                  <dd data-testid="business-texting-prefill-phone">
                    {prefill.business_phone || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--jc-text-soft)]">Business email</dt>
                  <dd data-testid="business-texting-prefill-email">
                    {prefill.business_email || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--jc-text-soft)]">Business address</dt>
                  <dd data-testid="business-texting-prefill-address">
                    {formatAddress(prefill.business_address) || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--jc-text-soft)]">Business website</dt>
                  <dd data-testid="business-texting-prefill-website">
                    {prefill.business_website || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--jc-text-soft)]">EIN</dt>
                  <dd data-testid="business-texting-prefill-ein">
                    {prefill.ein_present ? `••• •• ${prefill.ein_last4}` : "—"}
                  </dd>
                </div>
              </dl>
            </section>

            <section
              data-testid="business-texting-attestations-card"
              className="jc-card p-5 space-y-4"
            >
              <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-[var(--jc-blue)]" />
                <div className="font-semibold text-sm">SMS compliance attestations</div>
              </div>
              <p className="text-xs text-[var(--jc-text-soft)]">
                These four items are required by the mobile carriers.
                They are separate from your general business information
                (which we already have from your profile).
              </p>

              {/* Estimated monthly volume */}
              <div className="space-y-1">
                <label htmlFor="bt-volume" className="text-xs font-semibold">
                  Estimated monthly text volume <span className="text-red-600">*</span>
                </label>
                <input
                  id="bt-volume"
                  data-testid="bt-attest-volume"
                  type="number"
                  min="1"
                  value={form.estimated_monthly_volume}
                  onChange={setField("estimated_monthly_volume")}
                  placeholder="e.g. 250"
                  className="w-full text-sm rounded border border-[var(--jc-border)] px-2 py-1.5 focus:outline-none focus:border-[var(--jc-blue)]"
                />
                <p className="text-[11px] text-[var(--jc-text-soft)]">
                  Rough number of texts you expect to send to customers each month.
                </p>
              </div>

              {/* Opt-in method */}
              <div className="space-y-1">
                <div className="text-xs font-semibold">
                  How did the customer opt in? <span className="text-red-600">*</span>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(OPT_IN_LABELS).map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-start gap-2 text-sm"
                      data-testid={`bt-optin-option-${key}`}
                    >
                      <input
                        type="radio"
                        name="opt_in_method"
                        value={key}
                        checked={form.opt_in_method === key}
                        onChange={setField("opt_in_method")}
                        className="mt-0.5"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Opt-in details / script */}
              <div className="space-y-1">
                <label htmlFor="bt-details" className="text-xs font-semibold">
                  Consent details {form.opt_in_method === "other"
                    ? <span className="text-red-600">*</span>
                    : <span className="text-[var(--jc-text-soft)]">(optional)</span>}
                </label>
                <textarea
                  id="bt-details"
                  data-testid="bt-attest-details"
                  value={form.opt_in_details}
                  onChange={setField("opt_in_details")}
                  placeholder={
                    form.opt_in_method === "verbal"
                      ? "e.g. Verbal consent captured on intake call. Notes are logged in the project record."
                      : form.opt_in_method === "web_form"
                      ? "e.g. Consent checkbox on our online estimate request form."
                      : form.opt_in_method === "paper"
                      ? "e.g. Signed work authorization includes SMS consent line."
                      : form.opt_in_method === "point_of_sale"
                      ? "e.g. Customer provides mobile number at the appointment and consents verbally."
                      : "Describe how your customer agreed to receive texts."
                  }
                  rows={3}
                  maxLength={2000}
                  className="w-full text-sm rounded border border-[var(--jc-border)] px-2 py-1.5 focus:outline-none focus:border-[var(--jc-blue)]"
                />
              </div>

              {/* Age-gated declaration */}
              <div className="space-y-1">
                <div className="text-xs font-semibold">
                  Age-gated content declaration <span className="text-red-600">*</span>
                </div>
                <p className="text-[11px] text-[var(--jc-text-soft)]">
                  Is any content in your messaging program intended for
                  recipients 18 or older?
                </p>
                <div className="space-y-1">
                  <label
                    className="flex items-start gap-2 text-sm"
                    data-testid="bt-agegate-no"
                  >
                    <input
                      type="radio"
                      name="age_gated_declaration"
                      value="false"
                      checked={form.age_gated_declaration === "false"}
                      onChange={setField("age_gated_declaration")}
                      className="mt-0.5"
                    />
                    <span>No — my messaging is not age-gated.</span>
                  </label>
                  <label
                    className="flex items-start gap-2 text-sm"
                    data-testid="bt-agegate-yes"
                  >
                    <input
                      type="radio"
                      name="age_gated_declaration"
                      value="true"
                      checked={form.age_gated_declaration === "true"}
                      onChange={setField("age_gated_declaration")}
                      className="mt-0.5"
                    />
                    <span>Yes — my messaging contains age-gated content.</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--jc-border)]">
                <button
                  type="button"
                  data-testid="business-texting-save-btn"
                  disabled={saving}
                  onClick={handleSave}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold jc-btn-blue disabled:opacity-50"
                >
                  {saving && <Loader2 className="animate-spin" size={14} />}
                  Save attestations
                </button>
              </div>
            </section>

            {/* Public consent page preview */}
            {previewUrl && (
              <section
                data-testid="business-texting-consent-preview"
                className="jc-card p-5 space-y-2"
              >
                <div className="font-semibold text-sm">Public consent page</div>
                <p className="text-xs text-[var(--jc-text-soft)]">
                  Carriers require a public URL that documents your SMS
                  consent process. JobClose hosts this for you at:
                </p>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="business-texting-consent-link"
                  className="text-xs font-mono text-[var(--jc-blue)] hover:underline break-all inline-flex items-center gap-1"
                >
                  {previewUrl} <ExternalLink size={11} />
                </a>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
